import { canReadDomain, coreQueryKey } from '@/api/core/authorization';
import {
  activateNativeRoom,
  readAppliedMidtermSale,
  readNativeRoomOwnerships,
  readRoomPricing,
  readRoomPricingEdit,
  readTypePricingEdit,
  reverseNativeMidtermSale,
  setNativeMidtermSale,
  setRoomPricing,
  setTypePricing,
} from '@/api/core/native-midterm-preparation';
import type { NativeCatalogRoom, NativeTypeDetails } from '@/api/core/native-property-read';
import { readRoomSale, readRoomSales } from '@/api/core/room-sales';
import { isApiError } from '@/api/http/errors';
import { formatDateTimeWithOffset } from '@/lib/format';
import {
  Button,
  ConfirmDialog,
  DatePicker,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@mgrvtechgroup/admin-ds';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState, useSyncExternalStore } from 'react';
import { EzremsRoomBindingSection } from './ezrems-room-binding-section';
import {
  EzremsTransitionPreviewSection,
  pendingEzremsTransition,
} from './ezrems-transition-preview-section';
import type { PropertyRuntime } from './property-queries';
import {
  RoomFirstMoveInDateEditor,
  firstDateBlocked,
  useFirstDateOwner,
} from './room-first-move-in-date-editor';
import { hasRoomSalesReadAccess } from './room-sales-queries';
import { RoomSelectionSection } from './rooms/room-selection-section';

const dateValue = (value: Date | undefined) =>
  value
    ? `${String(value.getFullYear())}-${String(value.getMonth() + 1).padStart(2, '0')}-${String(value.getDate()).padStart(2, '0')}`
    : '';
const dateObject = (value: string) => (value ? new Date(`${value}T12:00:00`) : undefined);

const message = (error: unknown) =>
  isApiError(error) && error.status === 409
    ? '상태가 변경되었습니다. 최신 정보를 다시 조회한 뒤 새 명령으로 검토하세요.'
    : isApiError(error) &&
        (error.status === 503 || error.kind === 'network' || error.kind === 'timeout')
      ? '현재 결과를 확인할 수 없습니다. 재조회 후 상태를 확인하세요. 명령을 자동 재전송하지 않습니다.'
      : '요청을 완료하지 못했습니다. 현재 상태를 다시 확인하세요.';

const saleReasonLabels: Record<string, string> = {
  writer_grant_missing: '판매 변경 권한 없음',
  writer_not_ready: '판매 변경 기능 사용 불가',
  sale_not_configured: '판매 설정 없음',
  sale_not_available: '현재 판매 불가',
  room_not_selected: '관리 대상 선정 필요',
  payment_pinned: '진행 중인 결제 확인 필요',
  no_contiguous_availability: '연속 이용 가능 기간 없음',
  classification_not_midterm: '전체 슬롯의 중기 판매 분류 확인 필요',
};
const ownershipLabels = {
  DISCOVERED: '발견됨',
  ELIGIBILITY_CHECKED: '적격성 확인됨',
  FENCE_PREPARED: '외부 변경 차단 준비됨',
  BARRIER_CHECKED: '차단 확인됨',
  ACTIVE: '활성',
  SUSPENDED: '중지',
  DRAINING: '반환 진행',
  RETURN_READY: '반환 준비됨',
  RETURNED: '반환됨',
};

export function NativeMidtermPreparation({
  readEnabled = true,
  runtime,
  generation,
  type,
  rooms,
  catalogComplete,
  workspaceRoomSelection,
  onGoExternal,
}: {
  readEnabled?: boolean;
  runtime: PropertyRuntime;
  generation: number;
  type: NativeTypeDetails;
  rooms: NativeCatalogRoom[];
  catalogComplete: boolean;
  workspaceRoomSelection?: { id: string; onChange: (id: string) => void };
  /** 슬롯만 연결된 객실의 안내에서 외부 연동(슬롯 연결·전환)으로 이동한다. */
  onGoExternal?: () => void;
}) {
  const queryClient = useQueryClient();
  const executing = useRef(false);
  const providerSection = useRef<HTMLElement>(null);
  const typePriceSection = useRef<HTMLElement>(null);
  const resultSection = useRef<HTMLDivElement>(null);
  const heldReverse = useRef<{
    roomId: string;
    actorId: string;
    input: { command_id: string; expected_sale_revision: number; reason: string };
  } | null>(null);
  const [uncertainContext, setUncertainContext] = useState('');
  const [uncertain, setUncertain] = useState(false);
  const nativeUncertain = useRef(false);
  const [providerPending, setProviderPending] = useState<{
    roomId: string;
    roomName?: string;
  } | null>(() => pendingEzremsTransition(runtime, generation));
  const [localRoomId, setLocalRoomId] = useState<string>('');
  const roomId = workspaceRoomSelection?.id ?? localRoomId;
  const [price, setPrice] = useState('');
  const [costs, setCosts] = useState({ deposit_amount: '', cleaning_fee: '' });
  const [roomValues, setRoomValues] = useState({
    regular_amount_minor: '',
    deposit_amount: '',
    cleaning_fee: '',
  });
  const [roomInherited, setRoomInherited] = useState({
    regular_amount_minor: true,
    deposit_amount: true,
    cleaning_fee: true,
  });
  const [fieldError, setFieldError] = useState('');
  const [firstDate, setFirstDate] = useState('');
  const [salePrice, setSalePrice] = useState('');
  const [replaceSalePrice, setReplaceSalePrice] = useState(false);
  const salePriceValid = /^[1-9]\d*$/.test(salePrice) && Number.isSafeInteger(Number(salePrice));
  const [from, setFrom] = useState('');
  const [until, setUntil] = useState('');
  const [reasons, setReasons] = useState({ sale: '', price: '', 'room-price': '', reverse: '' });
  const [nativeBusy, setBusy] = useState(false);
  const dateOwner = useFirstDateOwner(runtime);
  const busy = nativeBusy || dateOwner.busy || dateOwner.held !== null;
  const [feedback, setFeedback] = useState('');
  const [bindingPreviewRevision, setBindingPreviewRevision] = useState(0);
  const [unitBindingCount, setUnitBindingCount] = useState<number | undefined>(undefined);
  const focusPricingError = (error: string) => {
    const input = document.getElementById(error.replace(':', '-'));
    input?.focus({ preventScroll: true });
    const label =
      input?.closest('label') ??
      (input?.id ? document.querySelector(`label[for="${input.id}"]`) : null);
    const scroll = label && Reflect.get(label, 'scrollIntoView');
    if (typeof scroll === 'function') scroll.call(label, { block: 'center' });
  };
  useEffect(() => {
    if (fieldError && readEnabled) focusPricingError(fieldError);
  }, [fieldError, readEnabled]);
  const [pendingAction, setPendingAction] = useState<
    'price' | 'room-price' | 'sale' | 'reverse' | 'activate' | null
  >(null);
  const auth = useSyncExternalStore(runtime.session.subscribe, runtime.session.getSnapshot);
  const lifetime = useRef({ active: true, epoch: 0, target: '' });
  const scope = `${runtime.apiNamespace}:${String(generation)}:${type.property_id}:${type.id}:${String(type.revision)}:${roomId}:${String(rooms.find((item) => item.id === roomId)?.revision)}`;
  if (lifetime.current.target !== scope) {
    lifetime.current.target = scope;
    lifetime.current.epoch++;
  }
  useEffect(() => {
    const ownedLifetime = lifetime.current;
    ownedLifetime.active = true;
    const unsubscribe = runtime.session.subscribe(() => {
      ownedLifetime.epoch++;
      setPendingAction(null);
      setBusy(false);
    });
    return () => {
      ownedLifetime.active = false;
      ownedLifetime.epoch++;
      unsubscribe();
    };
  }, [runtime.session, runtime.client]);
  useEffect(() => {
    setPendingAction(null);
    setFeedback('');
    setBusy(false);
  }, [scope]);
  const current = auth.generation === generation && auth.status === 'authorized';
  const readPrice =
    current &&
    Boolean(
      auth.authorization?.assigned &&
        auth.authorization.role_bits & 12 &&
        (auth.authorization.domain_bits.booking ?? 0) & 2,
    ) &&
    auth.authorization !== null &&
    !auth.deniedDomains.includes('booking') &&
    canReadDomain(auth.authorization, 'booking', 2);
  const writePrice =
    current &&
    Boolean(
      auth.authorization?.assigned &&
        auth.authorization.role_bits & 12 &&
        (auth.authorization.domain_bits.booking ?? 0) & 1,
    ) &&
    auth.authorization !== null &&
    !auth.deniedDomains.includes('booking') &&
    canReadDomain(auth.authorization, 'booking', 1);
  const writeSale =
    current &&
    auth.authorization !== null &&
    !auth.deniedDomains.includes('booking') &&
    canReadDomain(auth.authorization, 'booking', 32);
  const writeInventory =
    current &&
    auth.authorization !== null &&
    !auth.deniedDomains.includes('inventory') &&
    canReadDomain(auth.authorization, 'inventory', 8);
  const room = rooms.find((item) => item.id === roomId);
  const readOwnership =
    current &&
    auth.authorization !== null &&
    !auth.deniedDomains.includes('property') &&
    !auth.deniedDomains.includes('inventory') &&
    canReadDomain(auth.authorization, 'property', 1) &&
    canReadDomain(auth.authorization, 'inventory', 16);
  const ownershipQuery = useQuery({
    queryKey: [
      ...coreQueryKey(runtime.apiNamespace, generation, 'native-room-ownerships', roomId),
      room?.topology_revision,
      room?.selection_version,
    ],
    queryFn: ({ signal }) => {
      if (!room) throw new Error('선택한 객실이 없습니다.');
      return readNativeRoomOwnerships(runtime.client, room, signal);
    },
    enabled: readEnabled && Boolean(room) && readOwnership,
    retry: false,
    staleTime: 0,
    meta: { suppressGlobalError: true },
  });
  const activationReady =
    readOwnership &&
    ownershipQuery.isSuccess &&
    !ownershipQuery.isFetching &&
    ownershipQuery.data.units.every(
      (unit) => unit.ownership === null || unit.ownership.status === 'RETURNED',
    );
  const priceQuery = useQuery({
    queryKey: [
      ...coreQueryKey(runtime.apiNamespace, generation, 'native-midterm-price', roomId),
      type.id,
    ],
    queryFn: ({ signal }) => readRoomPricing(runtime.client, roomId, signal),
    enabled: readEnabled && Boolean(room) && readPrice,
    retry: false,
    staleTime: 0,
    meta: { suppressGlobalError: true },
  });
  const readEdit = async <T,>(read: () => Promise<T>) => {
    const epoch = lifetime.current.epoch;
    try {
      return await read();
    } catch (error) {
      if (
        lifetime.current.active &&
        epoch === lifetime.current.epoch &&
        lifetime.current.target === scope &&
        isApiError(error) &&
        error.status === 403
      )
        runtime.session.reportDomainAccessDenied('booking', generation);
      throw error;
    }
  };
  const typePricing = useQuery({
    queryKey: [
      ...coreQueryKey(runtime.apiNamespace, generation, 'native-type-pricing-edit', type.id),
    ],
    queryFn: ({ signal }) => readEdit(() => readTypePricingEdit(runtime.client, type.id, signal)),
    enabled: readEnabled && writePrice,
    retry: false,
    staleTime: 0,
    meta: { suppressGlobalError: true },
  });
  const roomPricing = useQuery({
    queryKey: [
      ...coreQueryKey(runtime.apiNamespace, generation, 'native-room-pricing-edit', roomId),
    ],
    queryFn: ({ signal }) => readEdit(() => readRoomPricingEdit(runtime.client, roomId, signal)),
    enabled: readEnabled && Boolean(room) && writePrice,
    retry: false,
    staleTime: 0,
    meta: { suppressGlobalError: true },
  });
  useEffect(() => {
    const policy = typePricing.data?.type_policy;
    setPrice(
      policy && !policy.deleted_at && policy.regular_amount_minor !== null
        ? String(policy.regular_amount_minor)
        : '',
    );
    setCosts({
      deposit_amount:
        policy && !policy.deleted_at && policy.deposit_amount !== null
          ? String(policy.deposit_amount)
          : '',
      cleaning_fee:
        policy && !policy.deleted_at && policy.cleaning_fee !== null
          ? String(policy.cleaning_fee)
          : '',
    });
  }, [typePricing.data]);
  useEffect(() => {
    const policy = roomPricing.data?.room_policy;
    setRoomInherited({
      regular_amount_minor:
        !policy || Boolean(policy.deleted_at) || policy.regular_amount_minor === null,
      deposit_amount: !policy || Boolean(policy.deleted_at) || policy.deposit_amount === null,
      cleaning_fee: !policy || Boolean(policy.deleted_at) || policy.cleaning_fee === null,
    });
    setRoomValues({
      regular_amount_minor:
        policy && !policy.deleted_at && policy.regular_amount_minor !== null
          ? String(policy.regular_amount_minor)
          : '',
      deposit_amount:
        policy && !policy.deleted_at && policy.deposit_amount !== null
          ? String(policy.deposit_amount)
          : '',
      cleaning_fee:
        policy && !policy.deleted_at && policy.cleaning_fee !== null
          ? String(policy.cleaning_fee)
          : '',
    });
  }, [roomPricing.data, roomId]);
  const saleQuery = useQuery({
    queryKey: [
      ...coreQueryKey(runtime.apiNamespace, generation, 'native-midterm-sale', roomId),
      type.property_id,
      type.id,
    ],
    queryFn: async ({ signal }) => {
      if (runtime.nativeRoomSalesRoomFilterEnabled !== true)
        throw new Error('선택 객실 판매 조회가 아직 연결되지 않았습니다.');
      const page = await readRoomSales(
        runtime.client,
        type.property_id,
        { limit: 1, cursor: null, roomId, roomTypeId: type.id },
        signal,
      );
      const entry = page.items.find((item) => item.room_id === roomId);
      if (entry) {
        if (
          entry.sale_revision === null &&
          entry.status === 'not_configured' &&
          entry.reasons.includes('sale_not_configured')
        )
          return {
            revision: 0,
            product: '미설정',
            firstMoveInDate: entry.first_move_in_date,
            detail: undefined,
            firstDateAllowed: false,
            allowed: entry.allowed_actions.includes('configure_native_midterm_sale'),
            pending: entry.pending_outcome,
            reasons: entry.reasons,
            reverseAllowed: false,
          };
        if (entry.sale_revision !== null) {
          const detail = await readRoomSale(runtime.client, roomId, signal);
          return {
            revision: detail.revision,
            product: detail.product,
            firstMoveInDate: detail.first_move_in_date,
            detail,
            firstDateAllowed:
              entry.allowed_actions.includes('change_first_move_in_date') &&
              detail.allowed_actions.includes('change_first_move_in_date'),
            allowed: entry.allowed_actions.includes('configure_native_midterm_sale'),
            pending: detail.pending_outcome,
            reasons: detail.reasons,
            reverseAllowed:
              entry.allowed_actions.includes('reverse_native_midterm_sale') &&
              detail.allowed_actions.includes('reverse_native_midterm_sale'),
          };
        }
        throw new Error('현재 판매 상태를 확인하지 못했습니다.');
      }
      throw new Error('선택한 객실의 판매 상태를 찾지 못했습니다.');
    },
    enabled:
      readEnabled &&
      runtime.nativeRoomSalesRoomFilterEnabled === true &&
      Boolean(room) &&
      hasRoomSalesReadAccess(runtime, generation),
    retry: false,
    staleTime: 0,
    meta: { suppressGlobalError: true },
  });
  const refresh = async () => {
    await Promise.all([
      readPrice ? priceQuery.refetch() : Promise.resolve(),
      writePrice ? typePricing.refetch() : Promise.resolve(),
      writePrice && room ? roomPricing.refetch() : Promise.resolve(),
      hasRoomSalesReadAccess(runtime, generation) ? saleQuery.refetch() : Promise.resolve(),
      readOwnership && room ? ownershipQuery.refetch() : Promise.resolve(),
    ]);
  };
  const pricingRaw = (forRoom: boolean) =>
    forRoom ? roomValues : { regular_amount_minor: price, ...costs };
  const pricingValues = (forRoom: boolean) => {
    const raw = pricingRaw(forRoom);
    return {
      regular_amount_minor:
        forRoom && roomInherited.regular_amount_minor ? null : Number(raw.regular_amount_minor),
      deposit_amount: forRoom && roomInherited.deposit_amount ? null : Number(raw.deposit_amount),
      cleaning_fee: forRoom && roomInherited.cleaning_fee ? null : Number(raw.cleaning_fee),
    };
  };
  const validatePricing = (action: 'price' | 'room-price') => {
    const raw = pricingRaw(action === 'room-price');
    const invalid = (Object.keys(raw) as (keyof typeof raw)[]).find(
      (key) =>
        !(action === 'room-price' && roomInherited[key]) &&
        (!/^\d+$/.test(raw[key]) ||
          !Number.isSafeInteger(Number(raw[key])) ||
          Number(raw[key]) < (key === 'regular_amount_minor' ? 1 : 0)),
    );
    const error = invalid
      ? `${action}:${invalid}`
      : !reasons[action].trim() || reasons[action].length > 500
        ? `${action}:reason`
        : '';
    setFieldError(error);
    if (error && error === fieldError) focusPricingError(error);
    return !error;
  };
  const amountLabel = (value: number | null | undefined, fallback = '미설정') =>
    value == null ? fallback : `${value.toLocaleString('ko-KR')}원`;
  const effectivePrice = priceQuery.data?.effective_fields.regular_amount_minor;
  const pricePolicy = [priceQuery.data?.room_policy, priceQuery.data?.type_policy].find(
    (policy) => policy?.id === effectivePrice?.source_policy_id,
  );
  const currentPriceLabel = readPrice
    ? priceQuery.isError
      ? '가격 조회 불가 · 현재 설정 미확인'
      : effectivePrice
        ? effectivePrice.reason === 'TYPE_POLICY_ABSENT'
          ? '가격 미설정'
          : effectivePrice.reason === 'LEGACY_UNKNOWN'
            ? '원본 가격 미확인'
            : effectivePrice.value !== null &&
                effectivePrice.value > 0 &&
                pricePolicy?.currency === 'KRW' &&
                pricePolicy.price_basis === 'DAY' &&
                pricePolicy.pricing_policy_code === 'MIDTERM'
              ? `${effectivePrice.value.toLocaleString()}원/일`
              : '가격 금액·통화·단위 미확인'
        : '조회 중'
    : '권한 없음';
  const currentSaleLabel = hasRoomSalesReadAccess(runtime, generation)
    ? saleQuery.isError
      ? '조회 불가'
      : saleQuery.data
        ? saleQuery.data.product
        : '조회 중'
    : runtime.salesReadEnabled === true
      ? '권한 없음'
      : '이 환경에서 판매 조회 준비 중';
  const currentFirstDateLabel = hasRoomSalesReadAccess(runtime, generation)
    ? saleQuery.isError
      ? '조회 불가 · 현재 날짜 미확인'
      : saleQuery.data
        ? (saleQuery.data.firstMoveInDate ?? '없음')
        : '미조회 · 현재 날짜 확인 중'
    : runtime.salesReadEnabled === true
      ? '권한 없음'
      : '이 환경에서 판매 조회 준비 중';
  const typePriceLabel = writePrice
    ? typePricing.isError
      ? '조회 불가'
      : typePricing.data?.type_policy && !typePricing.data.type_policy.deleted_at
        ? `${amountLabel(typePricing.data.type_policy.regular_amount_minor)} · 보증금 ${amountLabel(typePricing.data.type_policy.deposit_amount)} · 청소비 ${amountLabel(typePricing.data.type_policy.cleaning_fee)}`
        : typePricing.data
          ? '미설정'
          : '조회 불가'
    : '권한 없음';
  const execute = async (action: 'price' | 'room-price' | 'sale' | 'reverse' | 'activate') => {
    if (
      (!room && action !== 'price') ||
      executing.current ||
      firstDateBlocked(runtime) ||
      uncertain ||
      providerPending ||
      !current
    )
      return;
    const reason = action === 'activate' ? '' : reasons[action];
    if (action === 'activate' && !activationReady) return;
    const latest = runtime.session.getSnapshot();
    const domain = action === 'activate' ? 'inventory' : 'booking';
    const bit = action === 'activate' ? 8 : action === 'price' || action === 'room-price' ? 1 : 32;
    if (
      latest.generation !== generation ||
      latest.status !== 'authorized' ||
      !latest.authorization ||
      latest.deniedDomains.includes(domain) ||
      !canReadDomain(latest.authorization, domain, bit)
    )
      return;
    const epoch = lifetime.current.epoch;
    const owns = () =>
      lifetime.current.active &&
      lifetime.current.epoch === epoch &&
      lifetime.current.target === scope;
    const authorized = () => {
      const now = runtime.session.getSnapshot();
      return (
        owns() &&
        now.generation === generation &&
        now.status === 'authorized' &&
        now.authorization?.assigned &&
        (now.authorization.role_bits & 12) !== 0 &&
        !now.deniedDomains.includes(domain) &&
        ((now.authorization.domain_bits[domain] ?? 0) & bit) === bit
      );
    };
    if (!authorized()) return;
    setFeedback('');
    setFieldError('');
    if (action === 'price' || action === 'room-price') setPendingAction(null);
    const values = pricingValues(action === 'room-price');
    const observedSale = saleQuery.data;
    const observedType = typePricing.data;
    const observedRoom = roomPricing.data;
    if (action === 'price' || action === 'room-price') {
      if (!validatePricing(action)) return;
      const observed = action === 'price' ? observedType : observedRoom;
      if (
        !typePricing.isSuccess ||
        typePricing.isFetching ||
        (action === 'room-price' && (!roomPricing.isSuccess || roomPricing.isFetching)) ||
        observed?.target.property_id !== type.property_id ||
        (action === 'room-price' &&
          (observed.cas.target as { type_id?: string }).type_id !== type.id)
      )
        return;
      const policy = action === 'price' ? observedType?.type_policy : observedRoom?.room_policy;
      if (
        policy &&
        !policy.deleted_at &&
        (policy.currency !== 'KRW' ||
          policy.price_basis !== 'DAY' ||
          policy.pricing_policy_code !== 'MIDTERM')
      ) {
        setFeedback('현재 가격 정책의 통화·일 단위를 확인하세요.');
        return;
      }
    }
    if (
      action === 'sale' &&
      ((replaceSalePrice && !salePriceValid) ||
        (writePrice && (!roomPricing.isSuccess || roomPricing.isFetching)))
    ) {
      setFeedback('전환 일가격을 양의 정수 원 단위로 입력하세요.');
      return;
    }
    if (
      action === 'sale' &&
      (!saleQuery.isSuccess ||
        saleQuery.isFetching ||
        !observedSale ||
        !observedSale.allowed ||
        !firstDate)
    ) {
      setFeedback('현재 판매 상태와 입주 가능 첫날을 확인하세요.');
      return;
    }
    if (
      action === 'reverse' &&
      (!saleQuery.isSuccess ||
        saleQuery.isFetching ||
        observedSale?.product !== 'midterm' ||
        observedSale.revision <= 0 ||
        !observedSale.reverseAllowed ||
        observedSale.pending !== null ||
        !room?.selected ||
        !room.selection_version)
    ) {
      setFeedback('현재 선정 대상의 판매 상태와 미해결 요청을 다시 확인하세요.');
      return;
    }
    const selectionVersion = room?.selection_version;
    if (
      action === 'activate' &&
      (!room?.selected ||
        !selectionVersion ||
        !from ||
        !until ||
        new Date(until) <= new Date(from) ||
        !room.units.length)
    ) {
      setFeedback('관리 대상 선정, 전체 슬롯, 명시 관리 시작·종료 시각을 확인하세요.');
      return;
    }
    if ((!reason.trim() || reason.length > 500) && action !== 'activate') {
      setPendingAction(null);
      setFieldError(`${action}:reason`);
      setFeedback('해당 작업의 변경 사유를 입력하세요.');
      return;
    }
    const label =
      action === 'price'
        ? '객실 유형 일가격'
        : action === 'room-price'
          ? '객실 개별 일가격'
          : action === 'sale'
            ? '객실 중기 판매'
            : action === 'reverse'
              ? '객실 중기 판매 역전환'
              : '객실 전체 슬롯 관리권';
    setPendingAction(null);
    executing.current = true;
    setBusy(true);
    let pricingAccepted = false;
    let reverseAttempt: typeof heldReverse.current = null;
    try {
      if (!authorized()) return;
      const actor = await runtime.client.get<{ user_id?: unknown }>('users/me');
      const verified = runtime.session.getSnapshot();
      if (
        !authorized() ||
        typeof actor.user_id !== 'string' ||
        verified.generation !== generation ||
        verified.status !== 'authorized' ||
        !verified.authorization ||
        verified.deniedDomains.includes(domain) ||
        !canReadDomain(verified.authorization, domain, bit)
      )
        throw new Error('현재 수행자를 확인하세요.');
      if (pendingEzremsTransition(runtime, generation) || firstDateBlocked(runtime)) return;
      const command_id = crypto.randomUUID();
      if (action === 'price') {
        if (!observedType) return;
        await setTypePricing(runtime.client, type.id, {
          command_id,
          expected: observedType.cas,
          values,
          reason: reason.trim(),
        });
      } else if (action === 'room-price') {
        if (!observedRoom || !room) return;
        await setRoomPricing(runtime.client, room.id, {
          command_id,
          expected: observedRoom.cas,
          values,
          reason: reason.trim(),
        });
      } else if (action === 'sale') {
        if (!observedSale || !room) return;
        const preservedPricing = replaceSalePrice
          ? undefined
          : await readRoomPricing(runtime.client, room.id);
        if (!authorized()) return;
        if (preservedPricing) {
          const beforeWrite = await runtime.client.get<{ user_id?: unknown }>('users/me');
          if (!authorized() || beforeWrite.user_id !== actor.user_id)
            throw new Error('현재 수행자가 변경되었습니다.');
        }
        if (pendingEzremsTransition(runtime, generation) || firstDateBlocked(runtime)) return;
        const result = await setNativeMidtermSale(runtime.client, room.id, {
          command_id,
          expected_sale_revision: observedSale.revision,
          first_move_in_date: firstDate,
          ...(replaceSalePrice ? { daily_price_minor: Number(salePrice) } : {}),
          reason: reason.trim(),
        });
        pricingAccepted = true;
        if (!authorized()) return;
        await readAppliedMidtermSale(runtime.client, room.id, {
          preservedPricing,
          ...(replaceSalePrice ? { daily_price_minor: Number(salePrice) } : {}),
          first_move_in_date: firstDate,
          revision: Number(result.revision),
        });
      } else if (action === 'reverse') {
        if (!observedSale || !room) return;
        reverseAttempt = {
          roomId: room.id,
          actorId: actor.user_id,
          input: {
            command_id,
            expected_sale_revision: observedSale.revision,
            reason: reason.trim(),
          },
        };
        await reverseNativeMidtermSale(runtime.client, reverseAttempt.roomId, reverseAttempt.input);
      } else {
        if (!selectionVersion) return;
        await activateNativeRoom(runtime.client, room.id, {
          command_id,
          expected_topology_revision: room.topology_revision,
          expected_selection_revision: selectionVersion,
          unit_ids: room.units.map((unit) => unit.id),
          effective_from: new Date(from).toISOString(),
          effective_until: new Date(until).toISOString(),
        });
      }
      pricingAccepted = action === 'price' || action === 'room-price';
      if (!authorized()) return;
      if (action === 'price' || action === 'room-price') {
        const fresh =
          action === 'price'
            ? await readTypePricingEdit(runtime.client, type.id)
            : await readRoomPricingEdit(runtime.client, roomId);
        if (!authorized()) return;
        const observedTarget = (action === 'price' ? observedType : observedRoom)?.cas
          .target as Record<string, unknown>;
        const freshTarget = fresh.cas.target as Record<string, unknown>;
        const policy = 'room_policy' in fresh ? fresh.room_policy : fresh.type_policy;
        if (
          fresh.target.property_id !== type.property_id ||
          (fresh.cas.target as { type_id?: string }).type_id !== type.id ||
          Object.keys(observedTarget).some((key) => freshTarget[key] !== observedTarget[key]) ||
          !policy ||
          policy.deleted_at ||
          Object.entries(values).some(
            ([key, value]) => policy[key as keyof typeof policy] !== value,
          )
        ) {
          setUncertainContext(
            `객실 유형 ${type.name} · 객실 ${room?.name ?? '선택 없음'} · 변경 사유: ${reason}`,
          );
          nativeUncertain.current = true;
          setUncertain(true);
          setFeedback('명령 이후 현재 가격을 확인하지 못했습니다. 새 적용을 중단했습니다.');
          return;
        }
      }
      if (authorized()) setFeedback(`${label} 저장 후 현재 상태를 확인했습니다.`);
    } catch (error) {
      if (!authorized()) return;
      const resultUncertain =
        isApiError(error) &&
        (error.status === 503 ||
          (typeof error.status === 'number' && error.status >= 500) ||
          ['network', 'timeout', 'invalid-response'].includes(error.kind));
      if (resultUncertain || pricingAccepted) {
        setUncertainContext(
          `객실 유형 ${type.name} · 객실 ${room?.name ?? '선택 없음'} · 변경 사유: ${reason}`,
        );
        nativeUncertain.current = true;
        setUncertain(true);
        if (reverseAttempt) heldReverse.current = reverseAttempt;
      }
      if (isApiError(error) && error.status === 403)
        runtime.session.reportDomainAccessDenied(domain, generation);
      if (runtime.session.getSnapshot().generation === generation) setFeedback(message(error));
    } finally {
      executing.current = false;
      if (owns()) {
        setBusy(false);
        await queryClient.invalidateQueries({
          queryKey: coreQueryKey(
            runtime.apiNamespace,
            generation,
            'native-property-catalog',
            type.property_id,
          ),
        });
        if (authorized()) await refresh();
      }
    }
  };
  const retryHeldReverse = async () => {
    const held = heldReverse.current;
    if (!held || held.roomId !== room?.id || !current || busy || executing.current) return;
    const latest = runtime.session.getSnapshot();
    if (
      latest.generation !== generation ||
      latest.status !== 'authorized' ||
      !latest.authorization ||
      latest.deniedDomains.includes('booking') ||
      !canReadDomain(latest.authorization, 'booking', 32)
    )
      return;
    const epoch = lifetime.current.epoch;
    const owns = () =>
      lifetime.current.active &&
      lifetime.current.epoch === epoch &&
      lifetime.current.target === scope;
    const authorized = () => {
      const now = runtime.session.getSnapshot();
      return (
        owns() &&
        now.generation === generation &&
        now.status === 'authorized' &&
        now.authorization !== null &&
        !now.deniedDomains.includes('booking') &&
        canReadDomain(now.authorization, 'booking', 32)
      );
    };
    executing.current = true;
    setBusy(true);
    try {
      const actor = await runtime.client.get<{ user_id?: unknown }>('users/me');
      if (!authorized()) return;
      const verified = runtime.session.getSnapshot();
      if (
        actor.user_id !== held.actorId ||
        verified.generation !== generation ||
        verified.status !== 'authorized' ||
        !verified.authorization ||
        verified.deniedDomains.includes('booking') ||
        !canReadDomain(verified.authorization, 'booking', 32)
      ) {
        setFeedback('현재 수행자 또는 판매 권한이 달라 원래 요청을 다시 보낼 수 없습니다.');
        return;
      }
      await reverseNativeMidtermSale(runtime.client, held.roomId, held.input);
      if (!authorized()) return;
      heldReverse.current = null;
      nativeUncertain.current = false;
      setUncertain(false);
      setFeedback('원래 판매 해제 요청의 결과를 확인했습니다. 현재 판매 상태를 다시 확인하세요.');
    } catch (error) {
      if (!authorized()) return;
      if (isApiError(error) && error.status === 403)
        runtime.session.reportDomainAccessDenied('booking', generation);
      if (runtime.session.getSnapshot().generation === generation) setFeedback(message(error));
    } finally {
      executing.current = false;
      if (owns()) {
        setBusy(false);
        if (authorized()) await refresh();
      }
    }
  };
  const saleReasons =
    saleQuery.isSuccess && !saleQuery.isFetching
      ? saleQuery.data.reasons
          .map((reason) => saleReasonLabels[reason] ?? '추가 사유 확인 필요')
          .join(' · ')
      : '';
  const coreSaleBlock = (() => {
    if (uncertain || providerPending)
      return '이전 요청 결과를 확인해야 새 판매를 설정할 수 있습니다.';
    if (!writeSale) return 'Core 단독 판매 변경 권한이 없습니다.';
    if (!saleQuery.isSuccess || saleQuery.isFetching)
      return '현재 객실의 판매 상태를 먼저 확인하세요.';
    if (!saleQuery.data.allowed)
      return saleReasons || '현재 판매 상태에서는 Core 단독 설정을 요청할 수 없습니다.';
    if (!firstDate) return '첫 입주일을 선택하세요.';
    if (replaceSalePrice && !salePriceValid) return '새 판매 일가격을 양의 정수로 입력하세요.';
    if (!reasons.sale.trim()) return '판매 변경 사유를 입력하세요.';
    return 'Core 단독 판매 설정 내용을 검토하세요.';
  })();
  const typePriceForm = (
    <section
      ref={typePriceSection}
      tabIndex={-1}
      aria-label="객실 유형 기본 가격"
      className={
        room
          ? 'scroll-mt-[calc(var(--workspace-sticky-height,0px)+1rem)] space-y-4 rounded-md border p-4'
          : 'space-y-4'
      }
    >
      {room && (
        <>
          <h4 className="font-medium">{type.name} 유형 기본 가격</h4>
          <p className="body-sm text-muted-foreground">
            {type.name} 유형 가격을 사용하는 객실 전체에 적용합니다. 이 객실의 판매 설정과 별도로
            저장합니다.
          </p>
        </>
      )}
      {!writePrice && (
        <p className="body-sm text-muted-foreground">
          가격 변경 권한이 없어 금액을 수정할 수 없습니다.
        </p>
      )}
      <div className="grid gap-3 sm:grid-cols-2">
        <label className="body-sm grid gap-1">
          객실 유형 기본 일가격 (원)
          <Input
            id="price-regular_amount_minor"
            disabled={busy || !writePrice}
            aria-invalid={fieldError === 'price:regular_amount_minor'}
            aria-describedby={
              fieldError === 'price:regular_amount_minor' ? 'pricing-field-error' : undefined
            }
            type="number"
            min="1"
            value={price}
            onChange={(event) => {
              setPrice(event.target.value);
            }}
          />
        </label>
        {(['deposit_amount', 'cleaning_fee'] as const).map((key) => (
          <label className="body-sm grid gap-1" key={key}>
            객실 유형 {key === 'deposit_amount' ? '보증금' : '청소비'} (원)
            <Input
              id={`price-${key}`}
              disabled={busy || !writePrice}
              type="number"
              min="0"
              value={costs[key]}
              aria-invalid={fieldError === `price:${key}`}
              aria-describedby={fieldError === `price:${key}` ? 'pricing-field-error' : undefined}
              onChange={(event) => {
                setCosts({ ...costs, [key]: event.target.value });
              }}
            />
          </label>
        ))}
      </div>
      <label className="body-sm grid gap-1">
        유형 가격 변경 사유
        <Input
          id="price-reason"
          value={reasons.price}
          disabled={busy || uncertain || providerPending !== null}
          onChange={(event) => {
            setReasons({ ...reasons, price: event.target.value });
          }}
        />
      </label>
      <Button
        className="h-auto min-h-8 whitespace-normal"
        variant="outline"
        disabled={
          busy ||
          uncertain ||
          providerPending !== null ||
          !writePrice ||
          !typePricing.isSuccess ||
          typePricing.isFetching
        }
        onClick={() => {
          if (validatePricing('price')) setPendingAction('price');
        }}
      >
        객실 유형 일가격·비용 저장
      </Button>
    </section>
  );
  return (
    <div className="body-sm min-w-0 space-y-4 leading-relaxed">
      {providerPending && (
        <section aria-label="확인 중인 EZREMS 요청" className="space-y-2 rounded-md border p-4">
          <p role="status">
            {providerPending.roomName ?? '원래 객실'}의 EZREMS 전환 결과를 확인해야 새 변경을 요청할
            수 있습니다.
          </p>
          {workspaceRoomSelection &&
            providerPending.roomId &&
            providerPending.roomId !== roomId && (
              <Button
                variant="outline"
                onClick={() => {
                  workspaceRoomSelection.onChange(providerPending.roomId);
                }}
              >
                원래 요청 객실로 이동
              </Button>
            )}
        </section>
      )}
      {!workspaceRoomSelection && (
        <div className="body-sm grid gap-1">
          대상 객실
          <Select
            value={roomId || null}
            items={rooms.map((item) => ({
              value: item.id,
              label: `${item.name} · 슬롯 ${String(item.units.length)}개`,
            }))}
            onValueChange={(value) => {
              lifetime.current.epoch++;
              setLocalRoomId(value ?? '');
              setFeedback('');
            }}
          >
            <SelectTrigger aria-label="대상 객실" className="w-full sm:max-w-sm">
              <SelectValue placeholder="객실 선택" />
            </SelectTrigger>
            <SelectContent>
              {rooms.map((item) => (
                <SelectItem value={item.id} key={item.id}>
                  {item.name} · 슬롯 {item.units.length}개
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}
      {room && (
        <section aria-label="현재 객실 가격·판매 요약" className="space-y-3 rounded-md border p-4">
          <h3 className="font-semibold">{room.name} 현재 가격·판매 · 원/일</h3>
          <p className="body-sm">
            현재 가격: {currentPriceLabel} · 현재 판매: {currentSaleLabel}
          </p>
          <p className="body-sm">첫 입주 가능일: {currentFirstDateLabel}</p>
          <RoomFirstMoveInDateEditor
            runtime={runtime}
            generation={generation}
            roomId={room.id}
            roomName={room.name}
            propertyId={type.property_id}
            typeId={type.id}
            detail={saleQuery.data?.detail}
            ready={saleQuery.isSuccess && !saleQuery.isFetching && saleQuery.data.firstDateAllowed}
            active={readEnabled && current}
            blocked={nativeBusy || uncertain || providerPending !== null}
            isBlocked={() =>
              executing.current ||
              nativeUncertain.current ||
              pendingEzremsTransition(runtime, generation) !== null
            }
            refresh={refresh}
          />
          <p role="status" id="price-sales-next-step">
            {uncertain
              ? '이전 요청 결과를 먼저 확인하세요.'
              : priceQuery.isError || saleQuery.isError
                ? '현재 상태를 다시 조회하세요.'
                : effectivePrice?.reason === 'TYPE_POLICY_ABSENT'
                  ? '유형 기본 일가격을 설정한 뒤 판매를 검토하세요.'
                  : '외부 판매는 EZREMS 계약·전환 준비를 확인한 뒤 검토하세요.'}
          </p>
          <Button
            disabled={busy}
            aria-describedby="price-sales-next-step"
            onClick={() => {
              if (priceQuery.isError || saleQuery.isError) {
                void refresh();
                return;
              }
              const next = uncertain
                ? resultSection.current
                : effectivePrice?.reason === 'TYPE_POLICY_ABSENT'
                  ? typePriceSection.current
                  : providerSection.current;
              next?.scrollIntoView({ block: 'start' });
              next?.focus({ preventScroll: true });
            }}
          >
            {busy
              ? '처리 결과 확인 중'
              : uncertain
                ? '이전 요청 결과 확인'
                : priceQuery.isError || saleQuery.isError
                  ? '현재 상태 재확인'
                  : effectivePrice?.reason === 'TYPE_POLICY_ABSENT'
                    ? '유형 기본 일가격 설정으로 이동'
                    : 'EZREMS 전환 준비로 이동'}
          </Button>
          <p className="text-muted-foreground">
            객실 전체에 적용됩니다. 슬롯의 장기 월 가격은 별도로 설정합니다.
          </p>
          <p className="body-sm">
            객실 유형 가격: {typePriceLabel}{' '}
            {writePrice &&
              (typePricing.isSuccess
                ? `· 할인 규칙 ${String(typePricing.data.rules.length)}개`
                : '· 할인 규칙 미확인')}
          </p>
          <Button
            className="h-auto min-h-8 whitespace-normal"
            variant="outline"
            disabled={
              busy ||
              (!readPrice &&
                !writePrice &&
                !readOwnership &&
                !hasRoomSalesReadAccess(runtime, generation))
            }
            onClick={() => void refresh()}
          >
            현재 상태 다시 조회
          </Button>
        </section>
      )}
      {!room && workspaceRoomSelection && (
        <section aria-label="현재 유형 일가격" className="space-y-2 rounded-md border p-4">
          <h3 className="font-semibold">{type.name} 유형 기본 가격 · 원/일</h3>
          <p>{typePriceLabel}</p>
          <p>
            이 유형 가격을 사용하는 객실에 적용합니다. 유형 가격 저장에는 일가격·보증금·청소비가
            모두 필요합니다.
          </p>
          {typePriceForm}
        </section>
      )}
      {room && (
        <section
          ref={providerSection}
          tabIndex={-1}
          aria-label="EZREMS 연결·판매 전환"
          className="space-y-4 rounded-md border p-4"
        >
          <h3 className="font-semibold">EZREMS 연결·판매 전환</h3>
          <p>EZREMS 연결과 계약을 확인한 뒤 객실 전체의 판매 전환을 검토하세요.</p>
          <EzremsRoomBindingSection
            readEnabled={readEnabled}
            runtime={runtime}
            generation={generation}
            propertyId={type.property_id}
            typeId={type.id}
            typeName={type.name}
            room={room}
            onBindingChanged={() => {
              setBindingPreviewRevision((value) => value + 1);
            }}
            onUnitBindingCount={setUnitBindingCount}
            onGoSlots={onGoExternal}
          />
          {!catalogComplete && (
            <p className="body-sm">
              현재 객실 목록은 페이지 일부입니다. 전체 객실은 페이지를 이동해 확인하세요.
            </p>
          )}
          <>
            <p className="body-sm">
              전체 슬롯: {room.units.map((unit) => unit.name).join(', ') || '없음'} · 관리 대상:{' '}
              {room.selected ? '선정' : '미선정'}
            </p>
            <EzremsTransitionPreviewSection
              unitBindingCount={unitBindingCount}
              key={`${room.id}:${String(bindingPreviewRevision)}`}
              runtime={runtime}
              generation={generation}
              roomId={room.id}
              roomName={room.name}
              units={room.units}
              isNativeBlocked={() =>
                executing.current || nativeUncertain.current || firstDateBlocked(runtime)
              }
              onPendingChange={setProviderPending}
              onApplied={async () => {
                await refresh();
                await queryClient.invalidateQueries({
                  queryKey: coreQueryKey(
                    runtime.apiNamespace,
                    generation,
                    'native-property-catalog',
                    type.property_id,
                  ),
                });
              }}
            />
          </>
        </section>
      )}
      {room && (
        <>
          <section aria-label="현재 관리권" className="space-y-3 rounded-md border p-4">
            <h3 className="font-semibold">현재 관리권</h3>
            {readOwnership &&
              (ownershipQuery.isFetching ? (
                <p role="status">관리권을 확인하고 있습니다.</p>
              ) : ownershipQuery.isSuccess ? (
                <>
                  <ul>
                    {ownershipQuery.data.units.map((unit) => (
                      <li key={unit.unit_id}>
                        {room.units.find((item) => item.id === unit.unit_id)?.name}:{' '}
                        {unit.ownership
                          ? `${ownershipLabels[unit.ownership.status]} · ${formatDateTimeWithOffset(unit.ownership.effective_from)}부터 · ${unit.ownership.effective_until ? formatDateTimeWithOffset(unit.ownership.effective_until) : '종료 없음'}`
                          : '등록된 관리권 없음'}
                      </li>
                    ))}
                  </ul>
                  {!activationReady && (
                    <p>관리권이 이미 등록되어 있습니다. 다시 활성화하지 않습니다.</p>
                  )}
                </>
              ) : (
                <p role="alert">
                  현재 관리권을 확인하지 못했습니다. 현재 상태를 다시 조회해 주세요.
                </p>
              ))}
            {!readOwnership && <p>현재 관리권을 조회할 권한이 없습니다.</p>}
            <p className="caption text-muted-foreground">
              관리권과 관리기간 확인은 해당 기간의 판매 가능 여부를 뜻하지 않습니다.
            </p>
          </section>
          <section aria-label="가격·판매 수정" className="space-y-4">
            <h3 className="font-medium">가격·판매 수정</h3>
            <section aria-label="선택 객실 중기 판매" className="space-y-4 rounded-md border p-4">
              <h4 className="font-semibold">{room.name} Core 단독 중기 판매 설정</h4>
              <p>
                외부 PMS 계약 타입을 바꾸지 않는 별도 업무입니다. EZREMS 연결 실패·미연결에 대한
                대체 경로가 아니며, 연결된 객실은 EZREMS 계약 타입 전환을 사용하세요.
              </p>
              <p className="body-sm">
                적용 대상: {room.name} 객실 전체 · 슬롯 {room.units.length}개
              </p>
              <p>
                {replaceSalePrice
                  ? '새 일가격과 보증금 0원·청소비 0원을 첫 입주일과 함께 적용합니다.'
                  : '현재 일가격·보증금·청소비·할인 설정을 유지하며 판매와 첫 입주일만 설정합니다.'}
              </p>
              <div className="grid gap-3 sm:grid-cols-2">
                <label className="flex items-center gap-2 sm:col-span-2">
                  <input
                    type="checkbox"
                    checked={replaceSalePrice}
                    disabled={busy || uncertain || providerPending !== null}
                    onChange={(event) => {
                      setReplaceSalePrice(event.target.checked);
                    }}
                  />
                  새 판매 일가격 설정 (보증금·청소비 0원)
                </label>
                <label className="body-sm grid gap-1 sm:max-w-sm">
                  판매 전환 일가격 (원)
                  <Input
                    aria-label="판매 전환 일가격 (원)"
                    inputMode="numeric"
                    value={salePrice}
                    disabled={busy || uncertain || !replaceSalePrice}
                    onChange={(e) => {
                      setSalePrice(e.target.value);
                    }}
                  />
                </label>
                <div className="body-sm grid gap-1">
                  첫 입주일
                  <DatePicker
                    value={dateObject(firstDate)}
                    onChange={(date) => {
                      setFirstDate(dateValue(date));
                    }}
                  />
                </div>
                <label className="body-sm grid gap-1 sm:col-span-2">
                  변경 사유
                  <Input
                    id="sale-reason"
                    aria-invalid={fieldError === 'sale:reason'}
                    aria-describedby={
                      fieldError === 'sale:reason' ? 'pricing-field-error' : undefined
                    }
                    value={reasons.sale}
                    onChange={(event) => {
                      setReasons({ ...reasons, sale: event.target.value });
                    }}
                  />
                </label>
              </div>

              <p id="core-sale-block" className="body-sm" role="status">
                {coreSaleBlock}
              </p>
              <Button
                aria-describedby="core-sale-block"
                className="h-auto min-h-8 whitespace-normal"
                disabled={
                  busy ||
                  uncertain ||
                  providerPending !== null ||
                  !writeSale ||
                  !firstDate ||
                  !reasons.sale.trim() ||
                  reasons.sale.length > 500 ||
                  !saleQuery.isSuccess ||
                  saleQuery.isFetching ||
                  !saleQuery.data.allowed ||
                  (replaceSalePrice && !salePriceValid) ||
                  (writePrice && (!roomPricing.isSuccess || roomPricing.isFetching))
                }
                onClick={() => {
                  setPendingAction('sale');
                }}
              >
                중기 판매 설정
              </Button>
            </section>
            {typePriceForm}
            <section
              tabIndex={-1}
              aria-label="이 객실 가격"
              className="space-y-4 rounded-md border p-4"
            >
              <h4 className="font-medium">{room.name} 객실 가격</h4>
              <p className="body-sm text-muted-foreground">
                이 객실의 가격·비용을 따로 지정하거나 객실 유형 가격을 사용합니다.
              </p>
              <fieldset
                className="space-y-3 rounded-md border p-4 sm:col-span-2"
                disabled={busy || !writePrice}
              >
                <legend className="font-medium">객실 가격·비용</legend>
                <p className="body-sm text-muted-foreground">
                  항목마다 객실 유형 가격 사용을 선택할 수 있습니다. 비용 0원은 비용 없음입니다.
                  객실 유형의 미확인 비용은 직접 입력하세요.
                </p>
                {(['regular_amount_minor', 'deposit_amount', 'cleaning_fee'] as const).map(
                  (key) => (
                    <div key={key} className="grid gap-2 sm:grid-cols-2">
                      <label htmlFor={`room-price-${key}`} className="body-sm sm:col-span-2">
                        객실{' '}
                        {key === 'regular_amount_minor'
                          ? '일가격'
                          : key === 'deposit_amount'
                            ? '보증금'
                            : '청소비'}{' '}
                        (원)
                      </label>
                      <Input
                        id={`room-price-${key}`}
                        type="number"
                        min={key === 'regular_amount_minor' ? '1' : '0'}
                        disabled={roomInherited[key]}
                        value={roomValues[key]}
                        aria-invalid={fieldError === `room-price:${key}`}
                        aria-describedby={
                          fieldError === `room-price:${key}` ? 'pricing-field-error' : undefined
                        }
                        onChange={(event) => {
                          setRoomValues({ ...roomValues, [key]: event.target.value });
                        }}
                      />
                      <label className="body-sm flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={roomInherited[key]}
                          aria-label={`객실 ${key === 'regular_amount_minor' ? '일가격' : key === 'deposit_amount' ? '보증금' : '청소비'} 객실 유형 가격 사용`}
                          onChange={(event) => {
                            setRoomInherited({ ...roomInherited, [key]: event.target.checked });
                          }}
                        />
                        객실 유형 가격 사용
                      </label>
                    </div>
                  ),
                )}
              </fieldset>
              <label className="body-sm grid gap-1">
                객실 가격 변경 사유
                <Input
                  id="room-price-reason"
                  value={reasons['room-price']}
                  disabled={busy || uncertain || providerPending !== null}
                  onChange={(event) => {
                    setReasons({ ...reasons, 'room-price': event.target.value });
                  }}
                />
              </label>
              <Button
                className="h-auto min-h-8 whitespace-normal"
                variant="outline"
                disabled={
                  busy ||
                  uncertain ||
                  providerPending !== null ||
                  !writePrice ||
                  !typePricing.isSuccess ||
                  typePricing.isFetching ||
                  !roomPricing.isSuccess ||
                  roomPricing.isFetching
                }
                onClick={() => {
                  if (validatePricing('room-price')) setPendingAction('room-price');
                }}
              >
                이 객실 가격 저장
              </Button>
            </section>
            <RoomSelectionSection
              runtime={runtime}
              generation={generation}
              room={room}
              blocked={busy || uncertain || providerPending !== null}
              onSelected={() =>
                queryClient.invalidateQueries({
                  queryKey: coreQueryKey(
                    runtime.apiNamespace,
                    generation,
                    'native-property-catalog',
                    type.property_id,
                  ),
                })
              }
            />
            {!(
              ownershipQuery.isSuccess &&
              ownershipQuery.data.units.some((unit) => unit.ownership?.status === 'ACTIVE')
            ) &&
              (room.selected ? (
                <section aria-label="객실 관리기간" className="space-y-4 rounded-md border p-4">
                  <h4 className="font-medium">{room.name} 객실 관리기간</h4>
                  <p className="body-sm text-muted-foreground">
                    최초 활성화는 전체 슬롯의 새 관리권을 생성하며 관리기간 수정 기능이 아닙니다.
                    이미 활성 관리권이 있으면 다시 활성화할 필요가 없습니다. 가격과 첫 입주일은
                    관리기간과 별도로 입력합니다.
                  </p>
                  <div className="grid gap-3 sm:grid-cols-2">
                    <label className="body-sm grid gap-1">
                      관리 시작 시각
                      <Input
                        type="datetime-local"
                        disabled={!activationReady}
                        value={from}
                        onChange={(event) => {
                          setFrom(event.target.value);
                        }}
                      />
                    </label>
                    <label className="body-sm grid gap-1">
                      관리 종료 시각
                      <Input
                        type="datetime-local"
                        disabled={!activationReady}
                        value={until}
                        onChange={(event) => {
                          setUntil(event.target.value);
                        }}
                      />
                    </label>
                  </div>
                  <Button
                    className="h-auto min-h-8 whitespace-normal"
                    disabled={
                      busy ||
                      uncertain ||
                      providerPending !== null ||
                      !writeInventory ||
                      !room.selected ||
                      !room.selection_version ||
                      !activationReady
                    }
                    onClick={() => {
                      setPendingAction('activate');
                    }}
                  >
                    객실 전체 슬롯 최초 활성화
                  </Button>
                </section>
              ) : (
                <p className="body-sm text-muted-foreground">
                  관리 대상으로 선정된 객실만 최초 활성화할 수 있습니다.
                </p>
              ))}
            {(saleQuery.data?.product === 'midterm' ||
              uncertain ||
              providerPending !== null ||
              heldReverse.current !== null) && (
              <section aria-label="중기 판매 해제" className="space-y-4 rounded-md border p-4">
                <h4 className="font-medium">{room.name} 중기 판매 해제</h4>
                <p className="body-sm">Core 단독 판매 해제 · 현재 상태: {currentSaleLabel}</p>
                <p className="body-sm" role="status">
                  중기 판매 해제는 이 객실 전체에 적용되며 판매 상품은 장기, 전체 슬롯 분류는
                  일반으로 변경됩니다. 기존 가격과 예약·청구·결제 진행·출입 권한은 유지됩니다.
                  관리권 반환은 별도 업무입니다. EZREMS 일반 판매 복귀는 별도로 요청하세요.
                  {saleQuery.isSuccess && !saleQuery.isFetching && saleQuery.data.pending
                    ? ' 미해결 판매 요청이 있어 변경을 차단했습니다.'
                    : ''}
                </p>
                <p className="body-sm text-muted-foreground">
                  {saleQuery.isFetching || !saleQuery.isSuccess
                    ? '현재 판매 상태를 다시 조회해 주세요.'
                    : saleQuery.data.product === 'midterm'
                      ? saleQuery.data.reverseAllowed
                        ? '기존 예약을 유지하며 중기 판매를 해제합니다. 관리권 반환과 EZREMS 일반 판매 복귀는 별도입니다.'
                        : saleReasons || '현재 판매 상태에서는 해제할 수 없습니다.'
                      : '현재 중기 판매 중인 객실이 아닙니다.'}
                </p>
                <label className="body-sm grid gap-1">
                  판매 해제 사유
                  <Input
                    id="reverse-reason"
                    value={reasons.reverse}
                    disabled={busy || uncertain || providerPending !== null}
                    onChange={(event) => {
                      setReasons({ ...reasons, reverse: event.target.value });
                    }}
                  />
                </label>
                <Button
                  className="h-auto min-h-8 whitespace-normal"
                  variant="outline"
                  disabled={
                    busy ||
                    uncertain ||
                    providerPending !== null ||
                    !writeSale ||
                    !room.selected ||
                    !saleQuery.isSuccess ||
                    saleQuery.isFetching ||
                    saleQuery.data.product !== 'midterm' ||
                    saleQuery.data.revision <= 0 ||
                    saleQuery.data.pending !== null ||
                    !saleQuery.data.reverseAllowed
                  }
                  onClick={() => {
                    setPendingAction('reverse');
                  }}
                >
                  중기 판매 해제 검토
                </Button>
              </section>
            )}
          </section>
        </>
      )}
      {fieldError && (
        <p id="pricing-field-error" role="alert">
          {fieldError.endsWith(':reason')
            ? '해당 작업의 변경 사유를 1~500자로 입력하세요.'
            : '해당 항목에 원 단위 정수를 입력하세요. 일가격은 양수, 비용은 0 이상이어야 합니다.'}
        </p>
      )}
      <ConfirmDialog
        open={pendingAction !== null}
        onOpenChange={(open) => {
          if (!open) setPendingAction(null);
        }}
        target={pendingAction === 'price' ? type.name : (room?.name ?? type.name)}
        action={
          pendingAction === 'price'
            ? '객실 유형 일가격과 비용 저장'
            : pendingAction === 'room-price'
              ? '선택 객실 개별 가격과 비용 교정'
              : pendingAction === 'sale'
                ? '중기 판매 설정'
                : pendingAction === 'reverse'
                  ? '중기 판매 해제'
                  : '객실 전체 슬롯 최초 활성화'
        }
        consequence={
          pendingAction === 'price' || pendingAction === 'room-price'
            ? pendingAction === 'price'
              ? `${type.name}: ${amountLabel(Number(price))}/일 · 보증금 ${amountLabel(Number(costs.deposit_amount))} · 청소비 ${amountLabel(Number(costs.cleaning_fee))}`
              : `${room?.name ?? type.name}: 일가격 ${amountLabel(pricingValues(true).regular_amount_minor, '객실 유형 상속')} · 보증금 ${amountLabel(pricingValues(true).deposit_amount, '객실 유형 상속')} · 청소비 ${amountLabel(pricingValues(true).cleaning_fee, '객실 유형 상속')}`
            : pendingAction === 'sale'
              ? `${replaceSalePrice ? `새 일가격 ${amountLabel(Number(salePrice))}/일 · 보증금 0원 · 청소비 0원` : '일가격·보증금·청소비·할인 설정 유지'} · 첫 입주일 ${firstDate}`
              : pendingAction === 'reverse'
                ? `전체 슬롯 ${String(room?.units.length ?? 0)}개 · 기존 예약 유지 · 관리권 반환 별도`
                : `슬롯 ${String(room?.units.length ?? 0)}개 · ${from}부터 ${until || '종료 미입력'}`
        }
        variant="default"
        onConfirm={() => {
          if (pendingAction) void execute(pendingAction);
        }}
      />
      {uncertain && (
        <div ref={resultSection} tabIndex={-1} className="space-y-2">
          <p role="alert" className="body-sm">
            이전 명령 결과가 불확실하여 새 적용을 중단했습니다. 원래 대상: {uncertainContext}. 원래
            객실 유형·객실을 선택해 현재 상태 다시 조회로 가격·판매 상태를 확인하세요.
            조회·새로고침만으로 이전 명령의 성공을 확인할 수 없습니다. 가격 명령의 원래 결과는
            조회할 수 없어 적용 차단을 유지합니다. 원래 대상과 변경 사유를 보존해 담당자에게 확인을
            요청하세요.
          </p>
          {heldReverse.current?.roomId === room?.id && writeSale && (
            <Button
              className="h-auto min-h-8 whitespace-normal"
              variant="outline"
              disabled={busy}
              onClick={() => void retryHeldReverse()}
            >
              원래 판매 해제 요청 다시 확인
            </Button>
          )}
        </div>
      )}
      {busy && (
        <p role="status" aria-live="polite" className="body-sm">
          명령 처리 및 현재 상태 확인 중…
        </p>
      )}
      {feedback && (
        <p role="status" className="body-sm">
          {feedback}
        </p>
      )}
    </div>
  );
}
